import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { startCall, endCall, getTurnCredentials, getUserBalance } from '../utils/api';
import io from 'socket.io-client';
import Peer from 'simple-peer';
import { notify } from '../utils/notify';
import './VideoCall.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

function VideoCall() {
    const { userId: receiverId } = useParams();
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    
    const isInitiator = user.gender === 'male';
    
    const [stream, setStream] = useState(null);
    const [callId, setCallId] = useState(null);
    const [callDuration, setCallDuration] = useState(0);
    const [isFreeTrialActive, setIsFreeTrialActive] = useState(false);
    const [, setFreeDuration] = useState(0);
    const [remainingFreeDuration, setRemainingFreeDuration] = useState(0);
    const [, setUserBalance] = useState(0); // User's balance in seconds
    const [remainingBalance, setRemainingBalance] = useState(0); // Remaining balance during call
    const [showPackageModal, setShowPackageModal] = useState(false);
    const [callStatus, setCallStatus] = useState('connecting'); // 'connecting', 'ringing', 'active'
    const [, setCallAccepted] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    
    // Drag state
    const [videoPosition, setVideoPosition] = useState({ x: null, y: null });
    const [isDragging, setIsDragging] = useState(false);
    const dragInfoRef = useRef({ offsetX: 0, offsetY: 0 });
    
    const myVideo = useRef();
    const myVideoWrapperRef = useRef();
    const userVideo = useRef();
    const videoContainerRef = useRef();
    const connectionRef = useRef();
    const timerRef = useRef();
    const socketRef = useRef();
    const incomingSignalRef = useRef(null);
    const iceConfigRef = useRef(null);
    const turnReadyPromiseRef = useRef(null);
    const callIdRef = useRef(null); // Store callId in ref for reliable access
    const remainingFreeDurationRef = useRef(0);
    const remainingBalanceRef = useRef(0);
    const callDurationRef = useRef(0);
    const callStatusRef = useRef('connecting');
    const hasEndedCallRef = useRef(false);
    const acceptCallSentRef = useRef(false);
    const callUserSentRef = useRef(false);

    useEffect(() => {
        callStatusRef.current = callStatus;
    }, [callStatus]);

    const attachStreamToVideoEl = useCallback(async (videoEl, mediaStream, label) => {
        if (!videoEl || !mediaStream) return;
        try {
            videoEl.srcObject = mediaStream;
            // Best-effort autoplay; some browsers require a user gesture.
            const playPromise = videoEl.play?.();
            if (playPromise && typeof playPromise.then === 'function') {
                await playPromise;
            }
            console.log(`🎞️ Video element playing (${label})`, {
                tracks: mediaStream.getTracks().map(t => ({
                    kind: t.kind,
                    enabled: t.enabled,
                    readyState: t.readyState,
                    muted: t.muted
                }))
            });
        } catch (e) {
            console.warn(`🎞️ Video play() failed (${label})`, e);
        }
    }, []);

    // Initialize video position and attach drag handlers
    useEffect(() => {
        const setupDrag = () => {
            const wrapper = myVideoWrapperRef.current;
            const container = videoContainerRef.current;
            
            if (!wrapper || !container) {
                console.log('⏳ Waiting for wrapper/container...');
                setTimeout(setupDrag, 100);
                return;
            }
            
            console.log('✅ Wrapper found, setting up drag');
            
            // Initialize position
            const initPosition = () => {
                const containerRect = container.getBoundingClientRect();
                const wrapperRect = wrapper.getBoundingClientRect();
                
                const x = containerRect.width - wrapperRect.width - 20;
                const y = containerRect.height - wrapperRect.height - 100;
                
                setVideoPosition({ x: Math.max(0, x), y: Math.max(0, y) });
            };
            
            initPosition();
            window.addEventListener('resize', initPosition);
            
            // Drag handlers
            const handleMouseDown = (e) => {
                console.log('🖱️ MOUSE DOWN!', e);
                e.preventDefault();
                e.stopPropagation();
                e.cancelBubble = true;
                
                const wrapperRect = wrapper.getBoundingClientRect();
                
                dragInfoRef.current = {
                    offsetX: e.clientX - wrapperRect.left,
                    offsetY: e.clientY - wrapperRect.top
                };
                
                setIsDragging(true);
                
                // Prevent default video drag
                return false;
            };
            
            const handleTouchStart = (e) => {
                console.log('👆 TOUCH START!', e);
                e.preventDefault();
                e.stopPropagation();
                
                if (!e.touches[0]) return;
                
                const touch = e.touches[0];
                const wrapperRect = wrapper.getBoundingClientRect();
                
                dragInfoRef.current = {
                    offsetX: touch.clientX - wrapperRect.left,
                    offsetY: touch.clientY - wrapperRect.top
                };
                
                setIsDragging(true);
            };
            
            // Use capture phase to catch events early
            wrapper.addEventListener('mousedown', handleMouseDown, true);
            wrapper.addEventListener('touchstart', handleTouchStart, true);
            
            // Also add to video element itself as backup
            const video = myVideo.current;
            if (video) {
                video.addEventListener('mousedown', handleMouseDown, true);
                video.addEventListener('touchstart', handleTouchStart, true);
            }
            
            return () => {
                window.removeEventListener('resize', initPosition);
                wrapper.removeEventListener('mousedown', handleMouseDown, true);
                wrapper.removeEventListener('touchstart', handleTouchStart, true);
                if (video) {
                    video.removeEventListener('mousedown', handleMouseDown, true);
                    video.removeEventListener('touchstart', handleTouchStart, true);
                }
            };
        };
        
        const timeout = setTimeout(setupDrag, 100);
        
        return () => {
            clearTimeout(timeout);
        };
    }, []);

    // Keep React handlers for compatibility (but native ones are primary)
    const handleMouseDown = useCallback((e) => {
        console.log('🖱️ React mousedown fired (backup)');
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleTouchStart = useCallback((e) => {
        console.log('👆 React touchstart fired (backup)');
        e.stopPropagation();
    }, []);

    useEffect(() => {
        if (!isDragging) return;
        
        console.log('🔄 Dragging active, move handlers attached');

        const handleMove = (clientX, clientY) => {
            const container = videoContainerRef.current;
            const wrapper = myVideoWrapperRef.current;
            
            if (!container || !wrapper) return;
            
            const containerRect = container.getBoundingClientRect();
            const wrapperRect = wrapper.getBoundingClientRect();
            
            let newX = clientX - containerRect.left - dragInfoRef.current.offsetX;
            let newY = clientY - containerRect.top - dragInfoRef.current.offsetY;
            
            const maxX = containerRect.width - wrapperRect.width;
            const maxY = containerRect.height - wrapperRect.height;
            
            newX = Math.max(0, Math.min(newX, maxX));
            newY = Math.max(0, Math.min(newY, maxY));
            
            setVideoPosition({ x: newX, y: newY });
        };

        const handleMouseMove = (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleMove(e.clientX, e.clientY);
        };

        const handleTouchMove = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.touches[0]) {
                handleMove(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        const handleEnd = (e) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            console.log('🛑 Drag ended');
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove, { passive: false, capture: true });
        document.addEventListener('mouseup', handleEnd, { capture: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
        document.addEventListener('touchend', handleEnd, { capture: true });
        document.addEventListener('touchcancel', handleEnd, { capture: true });

        return () => {
            document.removeEventListener('mousemove', handleMouseMove, { capture: true });
            document.removeEventListener('mouseup', handleEnd, { capture: true });
            document.removeEventListener('touchmove', handleTouchMove, { capture: true });
            document.removeEventListener('touchend', handleEnd, { capture: true });
            document.removeEventListener('touchcancel', handleEnd, { capture: true });
        };
    }, [isDragging]);

    // This effect is intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // Load TURN server credentials
        turnReadyPromiseRef.current = getTurnCredentials()
            .then(config => {
                iceConfigRef.current = config.iceServers;
                console.log('TURN credentials loaded:', config.iceServers);
                return iceConfigRef.current;
            })
            .catch(err => {
                console.error('Failed to load TURN credentials:', err);
                // Fallback to default STUN servers
                iceConfigRef.current = [{ urls: 'stun:stun.l.google.com:19302' }];
                return iceConfigRef.current;
            });

        // Initialize socket first
        socketRef.current = io(SOCKET_URL, { auth: { userId: user.id } });
        console.log('🔌 VideoCall: Socket instance created, connected:', socketRef.current.connected, 'socketId:', socketRef.current?.id);

        // Emit user-online when socket connects
        const emitUserOnline = () => {
            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit('user-online', user.id);
                console.log('✅ User online emitted from VideoCall:', user.id, 'socketId:', socketRef.current.id);
            } else {
                console.warn('⚠️ Cannot emit user-online: socket not connected', {
                    socketExists: !!socketRef.current,
                    connected: socketRef.current?.connected
                });
            }
        };

        socketRef.current.on('connect', () => {
            console.log('✅ VideoCall socket connected:', socketRef.current.id);
            emitUserOnline();
        });

        socketRef.current.on('disconnect', () => {
            console.log('❌ VideoCall socket disconnected');
        });

        socketRef.current.on('connect_error', (error) => {
            console.error('❌ VideoCall socket connection error:', error);
        });

        // If socket is already connected, emit immediately
        if (socketRef.current.connected) {
            console.log('✅ Socket already connected, emitting user-online immediately');
            emitUserOnline();
        }

        // Wait for socket connection before proceeding with peer setup
        const waitForSocket = (callback) => {
            const checkConnection = () => {
                return socketRef.current && socketRef.current.connected;
            };
            
            if (checkConnection()) {
                console.log('✅ Socket already connected, proceeding immediately');
                callback();
            } else {
                console.log('⏳ Waiting for socket connection...', {
                    socketExists: !!socketRef.current,
                    connected: socketRef.current?.connected,
                    socketId: socketRef.current?.id
                });
                
                let attempts = 0;
                const maxAttempts = 50; // 5 seconds max
                
                const checkInterval = setInterval(() => {
                    attempts++;
                    if (checkConnection()) {
                        clearInterval(checkInterval);
                        console.log('✅ Socket connected after waiting, proceeding...', {
                            socketId: socketRef.current.id,
                            attempts
                        });
                        callback();
                    } else if (attempts >= maxAttempts) {
                        clearInterval(checkInterval);
                        console.error('❌ Socket connection timeout after', attempts, 'attempts');
                        // Still proceed with callback, but log warning
                        callback();
                    }
                }, 100);
            }
        };

        // Listen for call accepted event (for male user) - set up globally
        socketRef.current.on('call-accepted', (data) => {
            console.log('Call accepted event received (global listener):', data);
            console.log('Current connectionRef:', connectionRef.current);
            if (data && data.signal) {
                // Wait a bit for connectionRef to be set if it's not ready yet
                let retryCount = 0;
                const maxRetries = 50; // 5 seconds max wait
                const trySignal = () => {
                    if (connectionRef.current) {
                        try {
                            console.log('Initiator: Signaling peer with receiver signal');
                            connectionRef.current.signal(data.signal);
                            setCallAccepted(true);
                            // Status will be set to 'active' in peer.on('connect') event
                            console.log('Receiver signal sent to initiator peer, waiting for connection...');
                        } catch (error) {
                            console.error('Error signaling peer (global listener):', error);
                        }
                    } else if (retryCount < maxRetries) {
                        retryCount++;
                        console.warn(`ConnectionRef is null, retrying (${retryCount}/${maxRetries})...`);
                        setTimeout(trySignal, 100);
                    } else {
                        console.error('ConnectionRef is still null after max retries, call may not connect properly');
                    }
                };
                trySignal();
            }
        });

        // Listen for call rejected event
        socketRef.current.on('call-rejected', async (data) => {
            if (data.rejected) {
                await notify.info('Call was rejected');
                navigate('/male-dashboard');
            }
        });

        // Listen for call failed event (when receiver is not online or other error)
        socketRef.current.on('call-failed', async (data) => {
            console.error('❌ Call failed:', data);
            await notify.error('Call failed', data.message || 'Receiver may not be online.');
            navigate('/male-dashboard');
        });

        // Listen for call ended event (when other user ends the call)
        socketRef.current.on('call-ended', (data) => {
            console.log('Call ended by other user:', data);
            if (data.ended) {
                // End locally too (ensures billing + server cleanup is executed consistently)
                handleEndCall();
            }
        });

        // Initialize media stream
        navigator.mediaDevices.getUserMedia({ 
            video: true, 
            audio: true 
        }).then(currentStream => {
            console.log('Media stream obtained');
            setStream(currentStream);
            if (myVideo.current) {
                attachStreamToVideoEl(myVideo.current, currentStream, 'local');
            }
            
            // After stream is ready, initiate call or wait for incoming call
            if (isInitiator) {
                // Male user: initiate call after stream is ready
                initiateCall(currentStream);
            } else {
                // Female user: wait for incoming call signal or use stored signal
                // Wait for socket connection before creating peer connection
                waitForSocket(() => {
                    // Try to get signal from localStorage first (if coming from dashboard)
                    const storedSignal = localStorage.getItem('incomingCallSignal');
                    if (storedSignal) {
                        try {
                            const signal = JSON.parse(storedSignal);
                            console.log('Using stored signal from localStorage');
                            incomingSignalRef.current = signal;
                            createPeerConnectionAsReceiver(signal, currentStream);
                            // Clear stored signal (callerId will be cleared after signal is sent)
                            localStorage.removeItem('incomingCallSignal');
                        } catch (err) {
                            console.error('Error parsing stored signal:', err);
                        }
                    } else if (incomingSignalRef.current) {
                        // Signal already received, create connection
                        createPeerConnectionAsReceiver(incomingSignalRef.current, currentStream);
                    } else {
                        // Listen for incoming call signal (this shouldn't normally happen as signal should be in localStorage)
                        socketRef.current.on('incoming-call', (data) => {
                            console.log('Incoming call signal received in VideoCall:', data);
                            incomingSignalRef.current = data.signal;
                            // Store callerId if not already stored
                            if (data.callerId && !localStorage.getItem('incomingCallerId')) {
                                localStorage.setItem('incomingCallerId', data.callerId);
                            }
                            // Female user already accepted from dashboard, so create connection
                            createPeerConnectionAsReceiver(data.signal, currentStream);
                        });
                    }
                });
            }
        }).catch(err => {
            console.error('Failed to get media stream:', err);
            notify.error('Permission required', 'Please allow camera and microphone access.');
            navigate(user.gender === 'male' ? '/male-dashboard' : '/female-dashboard');
        });

        return () => {
            // Best-effort: ensure server clears busy/pending state even if the user leaves
            // the page or the peer connection drops without pressing "End Call".
            try {
                if (
                    !hasEndedCallRef.current &&
                    socketRef.current &&
                    socketRef.current.connected &&
                    (callStatusRef.current === 'ringing' || callStatusRef.current === 'active')
                ) {
                    socketRef.current.emit('end-call', {
                        userId: user.id,
                        otherUserId: receiverId
                    });
                }
            } catch (e) {
                // ignore best-effort cleanup errors
            }
            // Mark ended so peer 'close' callbacks don't re-trigger handleEndCall during unmount.
            hasEndedCallRef.current = true;

            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            if (connectionRef.current) {
                connectionRef.current.destroy();
            }
            // Don't emit user-offline here - let the dashboard handle online/offline status
            // Only disconnect the socket for this component
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initiateCall = async (currentStream) => {
        try {
            // Ensure we have ICE servers before creating the peer (TURN is often required on mobile networks).
            try {
                await (turnReadyPromiseRef.current || Promise.resolve());
            } catch (e) {
                // ignore
            }

            // Get user balance first - ALWAYS fetch fresh balance from server
            const balanceResponse = await getUserBalance(user.id);
            const balance = balanceResponse.balance || 0;
            console.log('💰 User balance loaded from server:', balance, 'seconds');
            
            // Store balance in both state and ref
            setUserBalance(balance);
            setRemainingBalance(balance);
            remainingBalanceRef.current = balance;
            
            const response = await startCall(user.id, receiverId);
            console.log('📞 Call started, callId received:', response.callId);
            setCallId(response.callId);
            callIdRef.current = response.callId; // Also store in ref for reliable access
            const receivedFreeDuration = response.freeDuration || 0;
            console.log('🎁 Free duration received:', receivedFreeDuration, 'seconds');
            setFreeDuration(receivedFreeDuration);
            setRemainingFreeDuration(receivedFreeDuration); // Initialize remaining free duration
            remainingFreeDurationRef.current = receivedFreeDuration;
            if (receivedFreeDuration > 0) {
                setIsFreeTrialActive(true);
            }
            setCallStatus('ringing'); // Show ringing status
            
            // Create peer connection with stream
            createPeerConnection(currentStream);
        } catch (error) {
            console.error('Failed to start call:', error);
            notify.error('Failed to start call', error?.message);
            navigate('/male-dashboard');
        }
    };

    const startCallTimer = async () => {
        // Timer starts only after call is accepted
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        
        // Reset call duration ref and state
        callDurationRef.current = 0;
        setCallDuration(0);
        
        // For male users (initiators), ALWAYS fetch fresh balance from server
        // Never trust the ref value as it may be stale from a previous call
        let initialBalance = 0;
        let initialFreeDuration = remainingFreeDurationRef.current;
        
        if (isInitiator) {
            try {
                // ALWAYS fetch fresh balance from server to ensure accuracy
                const balanceResponse = await getUserBalance(user.id);
                const serverBalance = balanceResponse.balance || 0;
                initialBalance = serverBalance;
                remainingBalanceRef.current = serverBalance;
                setRemainingBalance(serverBalance);
                console.log('⏱️ Timer start: Fresh balance from server:', serverBalance, 'seconds');
            } catch (error) {
                console.error('Error fetching balance for timer:', error);
                // Fallback to ref value if fetch fails, but this should rarely happen
                initialBalance = remainingBalanceRef.current;
                console.warn('⏱️ Using ref balance as fallback:', initialBalance, 'seconds');
            }
        } else {
            // For non-initiators, just use ref value
            initialBalance = remainingBalanceRef.current;
        }
        
        console.log('⏱️ Starting call timer. Initial Free trial:', initialFreeDuration, 'seconds. Initial Balance:', initialBalance, 'seconds');
        
        // Initialize display values
        if (initialFreeDuration > 0) {
            setRemainingFreeDuration(initialFreeDuration);
            setIsFreeTrialActive(true);
        } else {
            setRemainingBalance(initialBalance);
            setIsFreeTrialActive(false);
        }
        
        timerRef.current = setInterval(() => {
            // Update call duration using ref to avoid closure issues
            callDurationRef.current += 1;
            setCallDuration(callDurationRef.current);
            
            // Calculate remaining time based on call duration
            // This is for display only - actual balance is updated on server when call ends
            const currentCallDuration = callDurationRef.current;
            
            if (initialFreeDuration > 0 && currentCallDuration < initialFreeDuration) {
                // Free trial is active
                const remainingFree = initialFreeDuration - currentCallDuration;
                remainingFreeDurationRef.current = remainingFree;
                setRemainingFreeDuration(remainingFree);
                setIsFreeTrialActive(true);
            } else if (initialFreeDuration > 0 && currentCallDuration === initialFreeDuration) {
                // Free trial just ended, switch to balance
                console.log('🎁 Free trial ended, switching to balance. Balance:', initialBalance, 'seconds');
                setIsFreeTrialActive(false);
                setRemainingFreeDuration(0);
                remainingFreeDurationRef.current = 0;
                // Start using balance
                remainingBalanceRef.current = initialBalance;
                setRemainingBalance(initialBalance);
            } else {
                // Free trial ended (or never existed), calculate remaining balance
                setIsFreeTrialActive(false);
                const freeDurationUsed = initialFreeDuration;
                const chargeableDuration = currentCallDuration - freeDurationUsed;
                const remainingBalance = Math.max(0, initialBalance - chargeableDuration);
                
                remainingBalanceRef.current = remainingBalance;
                setRemainingBalance(remainingBalance);
                
                if (remainingBalance === 0 && currentCallDuration > 0) {
                    // Balance exhausted
                    console.log('⚠️ Balance exhausted, ending call');
                    setShowPackageModal(true);
                    handleEndCall();
                }
            }
        }, 1000);
    };

    const createPeerConnection = (currentStream) => {
        if (!currentStream) {
            console.error('No stream available for peer connection');
            return;
        }
        
        const config = {
            initiator: true,
            trickle: false,
            stream: currentStream
        };
        
        // Add TURN/STUN server configuration if available
        if (iceConfigRef.current) {
            config.config = {
                iceServers: iceConfigRef.current
            };
            console.log('🧊 Using ICE servers (initiator):', iceConfigRef.current);
        } else {
            console.warn('🧊 ICE servers not ready (initiator). Using browser defaults.');
        }
        
        const peer = new Peer(config);

        peer.on('signal', (data) => {
            console.log('📞 Initiator peer signal generated, preparing to emit call-user:', { callerId: user.id, receiverId: receiverId });
            if (callUserSentRef.current) return;
            const payload = { callerId: user.id, receiverId: receiverId, signal: data };
            const emit = () => {
                if (callUserSentRef.current) return;
                callUserSentRef.current = true;
                socketRef.current.emit('call-user', payload);
                console.log('✅ call-user event emitted successfully, socketId:', socketRef.current.id);
            };
            if (socketRef.current && socketRef.current.connected) {
                emit();
            } else if (socketRef.current) {
                console.warn('⚠️ Socket not connected yet, will emit call-user on connect');
                socketRef.current.once('connect', () => {
                    if (socketRef.current?.connected) emit();
                });
            }
        });

        peer.on('stream', (remoteStream) => {
            console.log('Initiator peer stream received');
            if (userVideo.current) {
                attachStreamToVideoEl(userVideo.current, remoteStream, 'remote/initiator');
            }
            // Some browsers never fire simple-peer 'connect' reliably for media-only calls.
            // Treat remote stream as "active".
            setCallStatus('active');
            setCallAccepted(true);
            if (!timerRef.current) {
                startCallTimer().catch(() => {});
            }
        });

        peer.on('connect', async () => {
            console.log('Initiator peer connection established');
            setCallStatus('active');
            setCallAccepted(true);
            // Start timer if not already started
            if (!timerRef.current) {
                await startCallTimer();
            }
        });

        peer.on('error', (err) => {
            console.error('Initiator peer error:', err);
        });

        peer.on('close', () => {
            console.log('Initiator peer connection closed');
            // If the connection closes unexpectedly, end the call to avoid stuck "busy"
            if (!hasEndedCallRef.current) {
                handleEndCall();
            }
        });

        connectionRef.current = peer;
    };

    const createPeerConnectionAsReceiver = async (signal, currentStream = null) => {
        // Ensure we have ICE servers before creating the peer (TURN is often required on mobile networks).
        try {
            await (turnReadyPromiseRef.current || Promise.resolve());
        } catch (e) {
            // ignore
        }

        const streamToUse = currentStream || stream;
        if (!streamToUse) {
            console.error('No stream available for receiver peer connection');
            return;
        }
        
        // Get callerId from localStorage if available, otherwise use receiverId from URL
        const callerId = localStorage.getItem('incomingCallerId') || receiverId;
        console.log('Creating receiver peer connection, callerId:', callerId, 'receiverId from URL:', receiverId);
        
        const config = {
            initiator: false,
            trickle: false,
            stream: streamToUse
        };
        
        // Add TURN/STUN server configuration if available
        if (iceConfigRef.current) {
            config.config = {
                iceServers: iceConfigRef.current
            };
            console.log('🧊 Using ICE servers (receiver):', iceConfigRef.current);
        } else {
            console.warn('🧊 ICE servers not ready (receiver). Using browser defaults.');
        }
        
        const peer = new Peer(config);

        peer.on('signal', (data) => {
            // Send acceptance signal back to caller
            console.log('📞 Receiver peer signal generated, preparing to send accept-call to callerId:', callerId);
            console.log('🔍 Checking socket status:', {
                socketExists: !!socketRef.current,
                connected: socketRef.current?.connected,
                socketId: socketRef.current?.id
            });

            if (acceptCallSentRef.current) return;
            const payload = { callerId: callerId, receiverId: user.id, signal: data };
            const emit = () => {
                if (acceptCallSentRef.current) return;
                acceptCallSentRef.current = true;
                socketRef.current.emit('accept-call', payload);
                console.log('✅ accept-call event emitted successfully, socketId:', socketRef.current.id);
                localStorage.removeItem('incomingCallerId');
            };

            if (socketRef.current && socketRef.current.connected) {
                emit();
            } else if (socketRef.current) {
                console.warn('⚠️ Receiver: socket not connected yet, will emit accept-call on connect');
                socketRef.current.once('connect', () => {
                    if (socketRef.current?.connected) emit();
                });
            } else {
                console.error('❌ Receiver: socketRef.current is NULL!');
            }
        });

        peer.on('stream', (remoteStream) => {
            console.log('Receiver peer stream received');
            if (userVideo.current) {
                attachStreamToVideoEl(userVideo.current, remoteStream, 'remote/receiver');
            }
            // Treat remote stream as "active" (see initiator comment).
            setCallStatus('active');
            setCallAccepted(true);
            if (!timerRef.current) {
                timerRef.current = setInterval(() => {
                    setCallDuration(prev => prev + 1);
                }, 1000);
            }
        });
        
        peer.on('connect', () => {
            console.log('Receiver peer connection established');
            setCallStatus('active');
            setCallAccepted(true);
            // Start timer for female user (they don't have free trial)
            if (!timerRef.current) {
                timerRef.current = setInterval(() => {
                    setCallDuration(prev => prev + 1);
                }, 1000);
            }
        });
        
        peer.on('error', (err) => {
            console.error('Receiver peer error:', err);
        });

        peer.on('close', () => {
            console.log('Receiver peer connection closed');
            // If the connection closes unexpectedly, end the call to avoid stuck "busy"
            if (!hasEndedCallRef.current) {
                handleEndCall();
            }
        });

        // Set connectionRef before signaling
        connectionRef.current = peer;
        
        // Signal the peer with the incoming signal
        if (signal) {
            console.log('Receiver: Signaling peer with incoming signal from initiator');
            try {
                peer.signal(signal);
                console.log('Receiver: Signal applied successfully');
            } catch (error) {
                console.error('Receiver: Error applying signal:', error);
            }
        } else {
            console.error('Receiver: No signal provided to peer');
        }
    };

    const cleanupAndNavigate = () => {
        // Clean up peer connection
        if (connectionRef.current) {
            connectionRef.current.destroy();
            connectionRef.current = null;
        }
        // Stop media stream
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        // Stop timer
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        // Reset call duration
        callDurationRef.current = 0;
        setCallDuration(0);
        // Reset callId ref (but keep state for logging)
        callIdRef.current = null;
        // Navigate back to dashboard
        navigate(user.gender === 'male' ? '/male-dashboard' : '/female-dashboard');
    };

    const handleEndCall = async () => {
        if (hasEndedCallRef.current) return;
        hasEndedCallRef.current = true;
        try {
            // Stop timer first to prevent further balance decrements
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            
            // Notify the other user that call ended
            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit('end-call', {
                    userId: user.id,
                    otherUserId: receiverId
                });
            }
            
            // CRITICAL: Only male users (initiators) should end the call on the server
            // Use ref value for callId as it's more reliable than state
            const currentCallId = callIdRef.current || callId;
            
            if (isInitiator) {
                console.log('🔍 Attempting to end call. callId (state):', callId, 'callId (ref):', callIdRef.current, 'isInitiator:', isInitiator);
                
                if (currentCallId) {
                    try {
                        // Use ref value for accurate duration (use the last known value)
                        const finalDuration = callDurationRef.current > 0 ? callDurationRef.current : callDuration;
                        
                        console.log('📞 Ending call. CallId:', currentCallId, 'Duration:', finalDuration, 'seconds');
                        console.log('📞 Current balance before call end:', remainingBalanceRef.current);
                        
                        const response = await endCall(currentCallId, finalDuration);
                        console.log('✅ Call ended. Server response:', response);
                        
                        // Update user balance from server response if available
                        if (response && response.callerBalance !== undefined) {
                            console.log('💰 Server updated balance to:', response.callerBalance, 'seconds');
                            console.log('💰 Expected balance:', remainingBalanceRef.current, 'seconds');
                            
                            // Update user object with new balance immediately
                            if (user && updateUser) {
                                const updatedUser = { ...user, balance: response.callerBalance };
                                updateUser(updatedUser);
                                console.log('💰 User object updated with new balance:', response.callerBalance, 'seconds');
                            }
                            
                            // CRITICAL: Update the ref so next call uses correct balance
                            remainingBalanceRef.current = response.callerBalance;
                            setRemainingBalance(response.callerBalance);
                            console.log('💰 remainingBalanceRef updated to:', response.callerBalance, 'seconds');
                        }
                    } catch (error) {
                        console.error('❌ Error ending call on server:', error);
                        console.error('   Error details:', error.response?.data || error.message);
                        // Continue anyway to navigate away
                    }
                } else {
                    console.error('⚠️ Cannot end call: callId is null in both state and ref!');
                    console.error('   callId (state):', callId);
                    console.error('   callIdRef.current:', callIdRef.current);
                    console.error('   isInitiator:', isInitiator);
                }
            } else {
                console.log('🔍 Not initiator, skipping server-side call end');
            }
        } catch (error) {
            console.error('Error in handleEndCall:', error);
        } finally {
            // Cleanup and navigate
            cleanupAndNavigate();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const toggleMute = () => {
        if (stream) {
            const audioTracks = stream.getAudioTracks();
            audioTracks.forEach(track => {
                track.enabled = isMuted;
            });
            setIsMuted(!isMuted);
        }
    };

    const toggleVideo = () => {
        if (stream) {
            const videoTracks = stream.getVideoTracks();
            videoTracks.forEach(track => {
                track.enabled = !isVideoEnabled;
            });
            setIsVideoEnabled(!isVideoEnabled);
        }
    };

    const switchCamera = async () => {
        try {
            if (!stream || !connectionRef.current) {
                console.error('No stream or connection available');
                return;
            }
            
            // Get current video track
            const videoTrack = stream.getVideoTracks()[0];
            if (!videoTrack) {
                console.error('No video track found');
                return;
            }
            
            // Get available devices
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            
            if (videoDevices.length < 2) {
                console.log('Only one camera available');
                notify.info('Camera', 'Only one camera is available on this device.');
                return;
            }
            
            // Get current device ID
            const currentDeviceId = videoTrack.getSettings().deviceId;
            console.log('Current camera device ID:', currentDeviceId);
            console.log('Available video devices:', videoDevices.map(d => ({ id: d.deviceId, label: d.label })));
            
            // Find next camera - try to find a different one
            let nextDeviceId = null;
            let nextIndex = -1;
            
            // First, try to find by deviceId match
            const currentIndex = videoDevices.findIndex(device => device.deviceId === currentDeviceId);
            if (currentIndex >= 0) {
                nextIndex = (currentIndex + 1) % videoDevices.length;
                nextDeviceId = videoDevices[nextIndex].deviceId;
            } else {
                // If current device not found in list, just use the first different one
                if (videoDevices.length > 0) {
                    nextIndex = 0;
                    nextDeviceId = videoDevices[0].deviceId;
                }
            }
            
            if (!nextDeviceId) {
                throw new Error('No alternative camera found');
            }
            
            console.log('Switching to camera device ID:', nextDeviceId, 'at index:', nextIndex);
            
            // Get new stream with next camera (video only, no audio)
            // Don't stop old track yet - we need it for replaceTrack
            let newStream;
            let newVideoTrack;
            let attempts = 0;
            const maxAttempts = 3;
            
            while (attempts < maxAttempts) {
                try {
                    // Try with ideal deviceId first (more compatible)
                    newStream = await navigator.mediaDevices.getUserMedia({
                        video: { deviceId: { ideal: nextDeviceId } }
                    });
                    newVideoTrack = newStream.getVideoTracks()[0];
                    
                    if (newVideoTrack) {
                        const newDeviceId = newVideoTrack.getSettings().deviceId;
                        console.log('Got new video track with device ID:', newDeviceId);
                        
                        // Verify it's a different camera
                        if (newDeviceId !== currentDeviceId) {
                            break; // Success - different camera
                        } else {
                            console.log('Same camera returned, trying next one');
                            newVideoTrack.stop();
                            newStream.getTracks().forEach(t => t.stop());
                            // Try next camera in list
                            nextIndex = (nextIndex + 1) % videoDevices.length;
                            nextDeviceId = videoDevices[nextIndex].deviceId;
                            attempts++;
                            continue;
                        }
                    }
                } catch (error) {
                    console.log(`Attempt ${attempts + 1} failed:`, error);
                    if (newStream) {
                        newStream.getTracks().forEach(t => t.stop());
                    }
                    attempts++;
                    if (attempts < maxAttempts) {
                        // Try next camera in list
                        nextIndex = (nextIndex + 1) % videoDevices.length;
                        nextDeviceId = videoDevices[nextIndex].deviceId;
                        await new Promise(resolve => setTimeout(resolve, 100));
                    } else {
                        throw error;
                    }
                }
            }
            
            if (!newVideoTrack) {
                throw new Error('Failed to get new video track after multiple attempts');
            }
            
            // Final verification
            const finalDeviceId = newVideoTrack.getSettings().deviceId;
            if (finalDeviceId === currentDeviceId) {
                newVideoTrack.stop();
                newStream.getTracks().forEach(t => t.stop());
                throw new Error('Could not switch to a different camera');
            }
            
            console.log('Successfully got new camera. Switching from', currentDeviceId, 'to', finalDeviceId);
            
            // Get RTCPeerConnection from simple-peer
            const pc = connectionRef.current._pc;
            if (pc) {
                const sender = pc.getSenders().find(s => 
                    s.track && s.track.kind === 'video'
                );
                
                if (sender) {
                    console.log('Replacing video track in RTCPeerConnection');
                    // Replace track in peer connection FIRST
                    await sender.replaceTrack(newVideoTrack);
                    console.log('Track replaced in RTCPeerConnection');
                } else {
                    console.error('No video sender found in RTCPeerConnection');
                    newVideoTrack.stop();
                    newStream.getTracks().forEach(t => t.stop());
                    throw new Error('No video sender found');
                }
            } else {
                console.error('No RTCPeerConnection found');
                newVideoTrack.stop();
                newStream.getTracks().forEach(t => t.stop());
                throw new Error('No RTCPeerConnection found');
            }
            
            // Replace video track in existing stream
            stream.removeTrack(videoTrack);
            stream.addTrack(newVideoTrack);
            
            // Update video element BEFORE stopping old track
            if (myVideo.current) {
                myVideo.current.srcObject = stream;
            }
            
            // NOW stop old video track (after new one is active)
            videoTrack.stop();
            
            // Stop the temporary stream (we only need the track)
            newStream.getTracks().forEach(track => {
                if (track !== newVideoTrack) {
                    track.stop();
                }
            });
            
            console.log('Camera switched successfully');
        } catch (error) {
            console.error('Error switching camera:', error);
            notify.error('Failed to switch camera', error?.message || 'Unknown error');
        }
    };

    return (
        <div className="video-call">
            <div className="video-container" ref={videoContainerRef}>
                <video 
                    ref={userVideo} 
                    autoPlay 
                    playsInline
                    className={`user-video ${callStatus === 'active' ? 'active' : ''}`} 
                />
                <div
                    ref={myVideoWrapperRef}
                    className={`my-video-wrapper ${isDragging ? 'dragging' : ''}`}
                    style={{
                        position: 'absolute',
                        ...(videoPosition.x !== null && videoPosition.y !== null ? {
                            left: `${videoPosition.x}px`,
                            top: `${videoPosition.y}px`,
                            bottom: 'auto',
                            right: 'auto'
                        } : {
                            bottom: '100px',
                            right: '20px'
                        }),
                        cursor: isDragging ? 'grabbing' : 'grab',
                        zIndex: 100
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    <video 
                        ref={myVideo} 
                        autoPlay 
                        muted
                        playsInline
                        draggable={false}
                        className={`my-video ${!isVideoEnabled ? 'video-disabled' : ''}`}
                    />
                </div>
                
                {callStatus === 'ringing' && isInitiator && (
                    <div className="call-status-overlay">
                        <div className="ringing-animation">
                            <div className="ring"></div>
                            <div className="ring"></div>
                            <div className="ring"></div>
                        </div>
                        <p className="status-text">Ringing...</p>
                        <p className="status-subtext">Waiting for answer</p>
                    </div>
                )}
            </div>

            <div className="call-controls">
                <div className="call-info">
                    <div className="call-timer">
                        {callStatus === 'active' ? formatTime(callDuration) : '00:00'}
                    </div>
                    {callStatus === 'active' && isInitiator && (
                        <div className="balance-countdown">
                            {isFreeTrialActive && remainingFreeDuration > 0 ? (
                                <span className="trial-badge">
                                    Free Trial: {formatTime(remainingFreeDuration)}
                                </span>
                            ) : remainingBalance > 0 ? (
                                <span className="balance-badge">
                                    Balance: {formatTime(remainingBalance)}
                                </span>
                            ) : (
                                <span className="balance-badge balance-empty">Balance: 00:00</span>
                            )}
                        </div>
                    )}
                    {callStatus === 'ringing' && (
                        <div className="call-status">Ringing...</div>
                    )}
                </div>
                <div className="call-controls-buttons">
                    {callStatus === 'active' && (
                        <>
                            <button 
                                className={`btn-control ${isMuted ? 'active' : ''}`} 
                                onClick={toggleMute}
                                title={isMuted ? 'Unmute' : 'Mute'}
                            >
                                {isMuted ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="currentColor"/>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" fill="currentColor"/>
                                    </svg>
                                )}
                            </button>
                            <button 
                                className={`btn-control ${!isVideoEnabled ? 'active' : ''}`} 
                                onClick={toggleVideo}
                                title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
                            >
                                {isVideoEnabled ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" fill="currentColor"/>
                                    </svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 6.5l-3.5-3.5L16 4.5 4 16.5l-1.5 1.5L2 19.5 4.5 22l1.5-1.5L19 8.5l1.5 1.5L22 11.5 19.5 14l-1.5-1.5L4 20.5H2v-2l13-13L13 2.5 10.5 0 7 3.5 4.5 1 2 3.5l3.5 3.5L2 10.5l2.5 2.5L7 10.5l3.5 3.5L13 11.5l3.5 3.5L20 11.5l2.5 2.5L25 11.5l-4-5z" fill="currentColor"/>
                                    </svg>
                                )}
                            </button>
                            <button 
                                className="btn-control" 
                                onClick={switchCamera}
                                title="Switch camera"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2.5L5.5 12 9 8.5V11h6V8.5l3.5 3.5-3.5 3.5z" fill="currentColor"/>
                                </svg>
                            </button>
                        </>
                    )}
                    <button className="btn-end-call" onClick={handleEndCall}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.33.16-.72.12-1.01-.1L4 12.9V19c0 .55-.45 1-1 1H1c-.55 0-1-.45-1-1V5c0-.55.45-1 1-1h2c.55 0 1 .45 1 1v3.38l2.83-1.72c.29-.18.68-.26 1.01-.1.33.16.56.51.56.9v3.1c1.45-.47 3-.72 4.6-.72s3.15.25 4.6.72v-3.1c0-.39.23-.74.56-.9.33-.16.72-.08 1.01.1L20 8.38V5c0-.55.45-1 1-1h2c.55 0 1 .45 1 1v14c0 .55-.45 1-1 1h-2c-.55 0-1-.45-1-1v-3.38l-2.83 1.72c-.29.18-.68.26-1.01.1-.33-.16-.56-.51-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" fill="currentColor"/>
                        </svg>
                        End Call
                    </button>
                </div>
            </div>

            {showPackageModal && (
                <div className="package-modal-overlay">
                    <div className="package-modal">
                        <h2>Free trial ended</h2>
                        <p>Purchase a minutes package to continue calling</p>
                        <button onClick={() => navigate('/male-dashboard')}>
                            Buy Minutes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default VideoCall;